import api from '../utils/baseUrl';
import {CampaignObject} from '../utils/types';
import axios from 'axios';

class CampaignService {
    async getAuthAdvertiserCampaigns(filters: {
        status?: string;
        search?: string;
        sortBy?: string;
        sortDirection?: 'asc' | 'desc';
    } = {}) {
        try {
            const response = await api.get('api/advertisers/campaigns', {params: filters});
            return Array.isArray(response.data.campaigns) ? response.data.campaigns : [];
        } catch (error) {
            console.error('Error fetching campaigns:', error);
            return [];
        }
    }

    async getActiveCampaigns(filters: {
        status?: string;
        search?: string;
        sortBy?: string;
        sortDirection?: 'asc' | 'desc';
    } = {}) {
        try {
            const response = await api.get('api/campaigns', {params: filters});
            return Array.isArray(response.data.campaigns) ? response.data.campaigns : [];
        } catch (error) {
            console.error('Error fetching campaigns:', error);
            return [];
        }
    }

    async getCampaignById(campaignId: string) {
        try {
            const response = await api.get(`api/campaigns/${campaignId}`);
            return response.data.campaign ?? {};
        } catch (error) {
            console.error('Error fetching campaign:', error);
            return [];
        }
    }

    async createCampaign(campaign: FormData) {
        try {
            return await api.post('/api/advertisers/campaigns', campaign, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        } catch (error) {
            console.error('Error creating campaign:', error);
            if (axios.isAxiosError(error) && error.response) {
                throw error;
            }
            throw error;
        }
    }

    async updateCampaign(campaignId: string, campaign: CampaignObject | FormData) {
        try {
            const config = campaign instanceof FormData 
                ? { headers: { 'Content-Type': 'multipart/form-data' } }
                : {};
            const response = await api.put(`/api/advertisers/campaigns/${campaignId}`, campaign, config);
            return response.data.campaign ?? {};
        } catch (error) {
            console.error('Error updating campaign:', error);
            if (axios.isAxiosError(error) && error.response) {
                throw error;
            }
            throw error;
        }
    }

    async updateActiveCampaign(campaignId: string, campaign: FormData) {
        try {
            const response = await api.put(`/api/advertisers/active/campaigns/${campaignId}`, campaign, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data.campaign ?? {};
        } catch (error) {
            console.error('Error updating active campaign:', error);
            if (axios.isAxiosError(error) && error.response) {
                throw error;
            }
            throw error;
        }
    }

    async cancelCampaign(campaignId: string) {
        try {
            return await api.delete(`/api/advertisers/campaigns/${campaignId}`);
        } catch (error) {
            console.error('Error cancelling campaign:', error);
            if (axios.isAxiosError(error) && error.response) {
                return error.response;
            }
        }
    }

    async promoteCampaign(campaignId: string) {
        try {
            return await api.post(`/api/campaigns/${campaignId}/promote`);
        } catch (error) {
            console.error('Error promoting campaign:', error);
            if (axios.isAxiosError(error) && error.response) {
                return error.response;
            }
        }
    }

    async redepositFromWallet(campaignId: string, amount: number) {
        try {
            return await api.post(`/api/campaigns/${campaignId}/redeposit`, {amount});
        } catch (error) {
            console.error('Error redepositing from wallet:', error);
            if (axios.isAxiosError(error) && error.response) {
                return error.response;
            }
        }
    }

     async getCampaignPromoters(campaignId: string, filters: {
         userType?: string;
         search?: string;
         sortBy?: string;
         sortDirection?: 'asc' | 'desc';
     } = {}) {
        try {
            const res = await api.get(`/api/campaigns/${campaignId}/promoters`, {
                params: filters
            });
            return res.data;
        } catch (error) {
            console.error('Error getting promoters of campaign:', error);
            if (axios.isAxiosError(error) && error.response) {
                return error.response;
            }
        }
    }

    async requestPromotion(campaignId: string) {
        return api.post(`/api/campaigns/${campaignId}/promote/request`);
    }

    async removeCampaignLink(
        campaignLinkId: string,
        payload: {
            reason: 'low_performance' | 'fraud' | 'spam' | 'other' | 'deactivated_by_user';
            reasonMessage?: string;
        }
    ) {
        try {
            return await api.post(
                `/api/campaignLinks/${campaignLinkId}/remove`,
                payload
            );
        } catch (error) {
            console.error('Error removing campaign link:', error);

            if (axios.isAxiosError(error) && error.response) {
                return error.response;
            }

            throw error;
        }
    }
}

export default new CampaignService();